import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { statefulPost } from '@api-client/csrf';
import { useStore } from '../../../hooks/useStore';
import { handleLoginResponse } from '../../../utils/handleLoginResponse';
import {
  Button,
  Heading,
  Modal,
  ModalStage,
  PasswordInput,
  Text,
  useForm
} from 'stubben-edge-unified-frontend-react-components';

interface Props {
  apiUrl: string;
  title: string;
  email: string;
  bearerToken: string;
  company: any;
}

const SignInStep: FC<Props> = ({
  apiUrl,
  title,
  email,
  bearerToken,
  company
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get('uuid');
  const { companyUuid } = useParams<{ companyUuid: string }>();
  const showTenancyNotHereHint = Boolean(uuid || companyUuid);

  const [loading, setLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [passwordResetSent, setPasswordResetSent] = useState<boolean>(false);
  const [passwordResetStatus, setPasswordResetStatus] = useState<string>('');
  const [isNotFrontEndUser, setIsNotFrontEndUser] = useState(false);

  const { authStore } = useStore();

  const {
    formData,
    register,
    validate,
    errors,
    addError,
    clearErrors,
    getErrorMessage
  } = useForm({
    password: ''
  });

  const handleOpen = () => {
    setIsOpen(true);
    setPasswordResetStatus('');
    setPasswordResetSent(false);
  };

  const handleSubmit = async () => {
    const { hasErrors } = validate();
    if (hasErrors) return;

    setLoading(true);

    try {
      const res = await statefulPost(`${apiUrl}/login`, {
        email,
        password: formData.password,
        company_uuid: company.uuid
      }, {
        Authorization: `Bearer ${bearerToken}`
      });
      await handleLoginResponse(res, authStore, setIsNotFrontEndUser);
    } catch (err: any) {
      toast.error('Oops, something went wrong!');

      const errorMessage =
        err.response?.data?.details ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        'An unknown error occurred';

      addError('password', errorMessage, true);
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    setLoading(true);

    try {
      const res = await statefulPost(
        `${apiUrl}/api/v1/password/email`,
        {
          company_uuid: company.uuid,
          email: email
        },
        {
          Authorization: `Bearer ${bearerToken}`
        }
      );
      setPasswordResetSent(true);
      setPasswordResetStatus(res.data.status);
    } catch (err: any) {
      setPasswordResetStatus(err.response?.data?.status);
      toast.error('Oops, something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordBlur = (hasError?: boolean) => {
    if (hasError) {
      addError('password', true);
    } else {
      clearErrors('password');
    }
  };

  return isNotFrontEndUser ? (
    <div className="flex h-full w-full items-center text-center">
      <Text variant="tertiary">
        This user is not allowed to use Flight deck.
      </Text>
    </div>
  ) : (
    <>
      <div className="space-y-6">
        <Heading size="xs" testIdentifier="login-sign-in-heading">
          {title}
        </Heading>

        <div
          className="flex items-center space-x-4 rounded-lg border border-[#D0D5DD] bg-white p-4"
          data-testid="login-sign-in-company"
        >
          <img
            src={
              company.icon
                ? company.icon
                : `https://ui-avatars.com/api/?name=${company.name.charAt(
                    0
                  )}&color=2A54EB&background=F1F4FF`
            }
            className="h-10 w-10 rounded-full object-contain"
          />
          <Heading size="xxs">{company.name}</Heading>
        </div>

        <div className="space-y-4">
          <PasswordInput
            {...register('password', { required: 'Must enter a password' })}
            autoComplete="current-password"
            onBlur={handlePasswordBlur}
            error={!!errors['password']}
            errorMessage={getErrorMessage('password')}
            testIdentifier="login-sign-in-password"
          />

          <Button
            variant="link"
            onClick={handleOpen}
            testIdentifier="login-sign-in-forgot-password"
          >
            Forgot password?
          </Button>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={!!errors['password']}
          loading={loading}
          size="lg"
          fullWidth
          testIdentifier="login-sign-in-submit"
        >
          Sign in
        </Button>

        {showTenancyNotHereHint && (
          <div
            className="space-y-2 text-sm text-[#565E73]"
            data-testid="login-sign-in-tenancy-not-here"
          >
            <Text>
              Tenancy not here.{' '}
              <a
                href="/login"
                className="text-[#2A54EB]"
                data-testid="login-sign-in-tenancy-not-here-link"
              >
                Go back to login
              </a>
            </Text>
          </div>
        )}
      </div>

      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        testIdentifier="login-forgot-password-modal"
      >
        <ModalStage
          actions={
            <>
              <Button
                onClick={() => setIsOpen(false)}
                variant="secondary"
                fullWidth
                testIdentifier="login-forgot-password-cancel"
              >
                {passwordResetSent ? 'Close' : 'Cancel'}
              </Button>
              {!passwordResetSent && (
                <Button
                  onClick={forgotPassword}
                  loading={loading}
                  fullWidth
                  testIdentifier="login-forgot-password-submit"
                >
                  Send reset email
                </Button>
              )}
            </>
          }
          title="Forgot password"
          testIdentifier="login-forgot-password-stage"
        >
          <Text>
            {passwordResetStatus
              ? passwordResetStatus
              : "We'll send you an email with instructions to reset your password. Please check your inbox and follow the steps provided to create a new password."}
          </Text>
        </ModalStage>
      </Modal>
    </>
  );
};

export default SignInStep;
